import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const pincodeLabel = (factory) => {
  if (factory?.trustedPincode) {
    return { label: 'Verified Pincode', value: factory.trustedPincode };
  }
  if (factory?.sourcePincode) {
    return { label: 'Source Pincode', value: factory.sourcePincode };
  }
  return null;
};

const badgeClass = 'text-[11px] px-2 py-0.5 rounded-full font-medium';

const FactoryList = ({
  factories,
  total,
  page,
  pageSize,
  loading,
  error,
  onSelect,
  onRetry,
  onPageChange,
}) => {
  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-24 rounded-lg border border-border bg-muted animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-error/30 bg-error/5 p-6 text-center">
        <p className="text-sm font-medium text-error mb-3">{error}</p>
        {onRetry && (
          <Button variant="default" size="sm" onClick={onRetry} iconName="RefreshCw" iconPosition="left">
            Retry
          </Button>
        )}
      </div>
    );
  }

  if (!factories?.length) {
    return (
      <div className="rounded-lg border border-border bg-card p-10 text-center shadow-sm">
        <Icon name="SearchX" size={32} className="mx-auto mb-2 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">No factories found.</p>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 50)));

  return (
    <div className="space-y-3">
      <p className="text-xs sm:text-sm text-muted-foreground">
        Showing {factories.length} of {total} {total === 1 ? 'factory' : 'factories'}
        {totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ''}
      </p>

      <div className="space-y-2">
        {factories.map((f) => {
          const pin = pincodeLabel(f);
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => onSelect(f)}
              className="w-full text-left rounded-lg border border-border bg-card p-4 shadow-sm transition-colors hover:border-primary hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm sm:text-base text-foreground truncate">
                    {f.factoryName}
                  </p>
                  {f.plantName && (
                    <p className="text-xs sm:text-sm mt-0.5 text-muted-foreground truncate">
                      {f.plantName}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {f.state && (
                      <span className={`${badgeClass} bg-muted text-foreground`}>
                        {f.state}
                      </span>
                    )}
                    {f.district && (
                      <span className={`${badgeClass} bg-muted text-foreground`}>
                        {f.district}
                      </span>
                    )}
                    {f.operationalStatus && (
                      <span className={`${badgeClass} bg-success/10 text-success`}>
                        {f.operationalStatus}
                      </span>
                    )}
                    {f.factoryNature && (
                      <span className={`${badgeClass} bg-primary/15 text-foreground`}>
                        {f.factoryNature}
                      </span>
                    )}
                    {f.millCode && (
                      <span className={`${badgeClass}`} style={{ background: 'rgba(30,74,114,0.1)', color: '#1e4a72' }}>
                        Mill {f.millCode}
                      </span>
                    )}
                    {pin && (
                      <span className={`${badgeClass}`} style={{ background: 'rgba(255,140,0,0.12)', color: '#9a3412' }}>
                        {pin.label}: {pin.value}
                      </span>
                    )}
                    <span className={`${badgeClass} bg-muted text-muted-foreground`}>
                      {f.managerCount} {f.managerCount === 1 ? 'contact' : 'contacts'}
                    </span>
                  </div>
                </div>
                <Icon name="ChevronRight" size={18} className="shrink-0 mt-1 text-muted-foreground" />
              </div>
            </button>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            iconName="ChevronLeft"
            iconPosition="left"
          >
            Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            iconName="ChevronRight"
            iconPosition="right"
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default FactoryList;
